// Where each seat sits on the town square. Seats are drawn clockwise in table order, rotated so
// that this phone's seat is at 6 o'clock: hold the phone flat in front of you and the circle
// matches the people around the table (the next seat clockwise is on your left).

export interface SeatPoint {
  seatId: string;
  index: number;
  x: number;
  y: number;
  /** Degrees clockwise from 12 o'clock. */
  angle: number;
}

export interface SeatLayout {
  size: number;
  ringRadius: number;
  nodeRadius: number;
  /** How far from a node's centre a tap still counts: past its edge, never into the next seat. */
  hitRadius: number;
  points: SeatPoint[];
}

const LABEL_SPACE = 16; // room under the lowest node for its name
const HIT_MARGIN = 6; // extra reach around each node for a finger

export function seatLayout(
  seatIds: string[],
  meSeatId: string | null,
  size = 320,
): SeatLayout {
  const n = seatIds.length;
  const center = size / 2;
  if (n === 0) return { size, ringRadius: 0, nodeRadius: 0, hitRadius: 0, points: [] };
  const meIndex = Math.max(0, meSeatId ? seatIds.indexOf(meSeatId) : 0);
  // node size shrinks as the table fills; the ring grows into the space that frees up
  let nodeRadius = 30;
  let ringRadius = center - nodeRadius - LABEL_SPACE;
  if (n > 1) {
    const fit = ((Math.PI * ringRadius) / n) * 0.78;
    nodeRadius = Math.max(13, Math.min(30, fit));
    ringRadius = center - nodeRadius - LABEL_SPACE;
  }
  const points = seatIds.map((seatId, index) => {
    const angle = n === 1 ? 180 : (180 + ((index - meIndex) * 360) / n + 360) % 360;
    const rad = (angle * Math.PI) / 180;
    return {
      seatId,
      index,
      angle,
      x: round(center + ringRadius * Math.sin(rad)),
      y: round(center - ringRadius * Math.cos(rad)),
    };
  });
  const halfGap = n > 1 ? ringRadius * Math.sin(Math.PI / n) : Infinity;
  const hitRadius = Math.min(nodeRadius + HIT_MARGIN, halfGap);
  return {
    size,
    ringRadius: round(ringRadius),
    nodeRadius: round(nodeRadius),
    hitRadius: round(hitRadius),
    points,
  };
}

function round(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Shorten a name to fit under a seat. */
export function shortName(name: string, max = 8): string {
  return name.length <= max ? name : `${name.slice(0, max - 1)}…`;
}
